import { useRef, useState, useCallback } from 'react';
import { useVoLoop } from './useVoLoop';

export function useAudioController(audioContextRef, audioBuffersRef) {
  // Source and track references
  const mainSourceRef = useRef(null);
  const voSourceRef = useRef(null);
  const gainMapRef = useRef(new Map());
  const volumeMapRef = useRef(new Map()); // Store original volumes
  const gainVoMapRef = useRef(new Map());
  const volumeVoMapRef = useRef(new Map()); // Store original volumes
  const currentTrackIdRef = useRef(null);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(null);
  const onFinishRef = useRef(null);



  // State
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const mutedRef = useRef(false); // Mute state
  const voGainNodeRef = useRef(null);

  // Get voice over functionality from useVoLoop
  const { 
    play: voPlay, 
    pause: voPause, 
    resume: voResume, 
    stop: voStop, 
    setVolume: voSetVolume,
    setupVoiceOver,
    cleanupVoiceOver
  } = useVoLoop(audioContextRef, voGainNodeRef);

  // Play a track with optional voice over
  const play = useCallback(({ 
    trackId, 
    offset = 0, 
    fadeIn = false, 
    voVolume = 0.7, 
    trackVolume = 0.7,
    preludeLength = null 
  }) => {
    setIsPaused(false);
    setIsPlaying(true);
    
    const context = audioContextRef.current;
    if (!context) {
      console.log("No audio context available");
      return;
    }
    
    const bufferObject = audioBuffersRef.current.get(trackId);
    const buffer = bufferObject?.buffer;
    const voBuffer = bufferObject?.voBuffer;
    const lyrics = bufferObject?.lyrics;
    
    // If preludeLength wasn't provided but we have lyrics, try to get it from there
    const effectivePreludeLength = preludeLength || (lyrics && lyrics[0]?.time);
    
    if (!buffer) {
      console.log("No audio buffer found for track:", trackId);
      return;
    }

    // Create audio sources and gain nodes
    const source = context.createBufferSource();
    const gainNode = context.createGain();
    const voGainNode = context.createGain();

    const initialVolume = fadeIn ? 0 : trackVolume;

    // Store volume settings
    volumeMapRef.current.set(trackId, initialVolume);
    volumeVoMapRef.current.set(trackId, voVolume);
    
    // Apply mute state if needed
    gainNode.gain.value = mutedRef.current ? 0 : initialVolume;
    voGainNode.gain.value = mutedRef.current ? 0 : voVolume;  

    // Set up main audio source
    source.buffer = buffer;
    source.connect(gainNode).connect(context.destination);
    
    try {
      source.start(0, offset);
      console.log('Playing track:', {
        trackId,
        hasVoBuffer: !!voBuffer,
        offset,
        fadeIn,
        preludeLength: effectivePreludeLength
      });
    } catch (error) {
      console.log("Error starting audio source:", error);
      return;
    }

    // Set up voice over if available
    if (voBuffer && effectivePreludeLength) {
      // Check if we're within the prelude
      if (offset < effectivePreludeLength) {
        // If we're within the prelude, use the looping setup with current offset
        console.log('Setting up voice over with prelude:', {
          offset,
          preludeLength: effectivePreludeLength
        });
        voPlay(voBuffer, offset, 0, voVolume, effectivePreludeLength, offset);
      }
    } else if (voBuffer) {
      // Just play the voice over once if no prelude length
      voPlay(voBuffer, offset, 0, voVolume);
    }
    
    // Apply fade-in if requested
    if (fadeIn && !mutedRef.current) {
      gainNode.gain.linearRampToValueAtTime(trackVolume, context.currentTime + 2);
    }

    // Store references
    mainSourceRef.current = source;
    voSourceRef.current = null; // Voice over is managed by useVoLoop
    gainMapRef.current.set(trackId, gainNode);
    gainVoMapRef.current.set(trackId, voGainNode);
    
    currentTrackIdRef.current = trackId;
    startTimeRef.current = context.currentTime - offset;
    pausedAtRef.current = null;
    
    // Set up onended handler
    source.onended = () => {
      if (mainSourceRef.current === source) {
        mainSourceRef.current = null;
        if (typeof onFinishRef.current === 'function') {
          onFinishRef.current(currentTrackIdRef.current);
        }
      }
    };
  }, [audioContextRef, audioBuffersRef, voPlay]);

  // Pause playback
  const pause = useCallback(() => {
    if (!mainSourceRef.current || !audioContextRef.current) return;
    
    try {
      // Calculate elapsed time for resume
      const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
      pausedAtRef.current = elapsed;
      
      // Stop main audio source
      try {
        mainSourceRef.current.stop();
      } catch (error) {
        console.log("Error stopping main source:", error);
      }
      
      mainSourceRef.current = null;
      setIsPaused(true);
      setIsPlaying(false);
      cleanupVoiceOver()
      
      // Pause voice over if playing
      if (isPlaying) {
        voPause();
      }
    } catch (error) {
      console.log("Error during pause:", error);
    }
  }, [audioContextRef, isPlaying, voPause]);

  // Stop playback completely
  const stop = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    
    // Stop voice over
    if (isPlaying) {
      voStop();
    }
    
    // Stop main audio source
    if (mainSourceRef.current) {
      try {
        mainSourceRef.current.stop();
      } catch (error) {
        console.log("Error stopping main source:", error);
      }
      mainSourceRef.current = null;
    }
    
    // Reset state
    pausedAtRef.current = null;
    currentTrackIdRef.current = null;
    startTimeRef.current = 0;
  }, [isPlaying, voStop]);

  // Resume paused playback
  const resume = useCallback((trackId) => {
    if (pausedAtRef.current == null) return;
    
    voResume();
    setIsPaused(false);
    setIsPlaying(true);
    play({ trackId, offset: pausedAtRef.current });
  }, [pausedAtRef, play, voResume]);

  // Seek to a specific time
  const seek = useCallback((trackId, time) => {
    // Stop current playback
    if (mainSourceRef.current) {
      try {
        mainSourceRef.current.stop();
      } catch (error) {
        console.log("Error stopping main source during seek:", error);
      }
    }
    
    // Clean up voice over
    cleanupVoiceOver();
    
    // Start playback from new position
    setIsPaused(false);
    setIsPlaying(true);
    
    // Get prelude length if available
    const bufferObject = audioBuffersRef.current.get(trackId);
    const voBuffer = bufferObject?.voBuffer;
    const lyrics = bufferObject?.lyrics;
    const preludeLength = lyrics?.[0]?.time;
    
    console.log('Seeking to:', {
      trackId,
      time,
      preludeLength,
      hasVoBuffer: !!voBuffer,
      withinPrelude: preludeLength && time < preludeLength
    });
    
    // Check if we're seeking within the prelude
    if (preludeLength && time < preludeLength && voBuffer) {
      play({ 
        trackId, 
        offset: time, 
        preludeLength 
      });
    } else {
      play({ trackId, offset: time });
    }
  }, [audioBuffersRef, cleanupVoiceOver, play]);

  // Fade to a new track
  const fadeTo = useCallback((newTrackId, duration = 2) => {
    const context = audioContextRef.current;
    const currentGain = gainMapRef.current.get(currentTrackIdRef.current);
    
    if (currentGain) {
      currentGain.gain.linearRampToValueAtTime(0, context.currentTime + duration);
    }
    
    setTimeout(() => {
      setIsPlaying(true);
      play({ trackId: newTrackId, fadeIn: true });
    }, duration * 1000);
  }, [audioContextRef, play]);

  // Set main track volume
  const setVolume = useCallback((trackId, value) => {
    const gain = gainMapRef.current.get(trackId);
    if (gain && audioContextRef.current) {
      volumeMapRef.current.set(trackId, value);
      gain.gain.setValueAtTime(
        mutedRef.current ? 0 : value, 
        audioContextRef.current.currentTime
      );
    }
  }, [audioContextRef]);

  // Set voice over volume
  const setVoVolume = useCallback((trackId, value) => {
    const gain = gainVoMapRef.current.get(trackId);
    if (gain && audioContextRef.current) {
      volumeVoMapRef.current.set(trackId, value);
      gain.gain.setValueAtTime(
        mutedRef.current ? 0 : value, 
        audioContextRef.current.currentTime
      );
    }
    
    // Also update the voice over volume
    voSetVolume(value);
  }, [audioContextRef, voSetVolume]);

  // Mute all audio
  const mute = useCallback(() => {
    mutedRef.current = true;
    setIsMuted(true);
    
    if (!audioContextRef.current) return;
    
    // Mute all main tracks
    for (const gain of gainMapRef.current.values()) {
      gain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
    }
    
    // Mute all voice overs
    for (const gain of gainVoMapRef.current.values()) {
      gain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
    }
    
    // Mute current voice over
    voSetVolume(0);
  }, [audioContextRef, voSetVolume]);

  // Unmute all audio
  const unmute = useCallback(() => {
    mutedRef.current = false;
    setIsMuted(false);
    
    if (!audioContextRef.current) return;
    
    // Restore main track volumes
    for (const [trackId, gain] of gainMapRef.current.entries()) {
      const volume = volumeMapRef.current.get(trackId) ?? 1;
      gain.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    }
    
    // Restore voice over volumes
    for (const [trackId, gain] of gainVoMapRef.current.entries()) {
      const volume = volumeVoMapRef.current.get(trackId) ?? 1;
      gain.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    }
    
    // Restore current voice over volume
    const currentTrackId = currentTrackIdRef.current;
    if (currentTrackId) {
      const voVolume = volumeVoMapRef.current.get(currentTrackId) ?? 0.7;
      voSetVolume(voVolume);
    }
  }, [audioContextRef, voSetVolume]);

  // Set callback for when track finishes
  const setOnFinish = useCallback((callback) => {
    onFinishRef.current = callback;
  }, []);

  return {
    play,
    isPlaying,
    pause,
    isPaused,
    stop,
    resume,
    seek,
    fadeTo,
    setVolume,
    setVoVolume,
    setOnFinish,
    mute,
    unmute,
    isMuted,
    startTimeRef,
    pausedAtRef,
    cleanupVoiceOver
  };
}
